// Alta de un cliente desde la nota o el punto de venta (como «Entidades: Mantención del Maestro» de
// Random cuando el código no existe). Solo lo que se necesita para venderle y emitirle: identidad
// (RUT + dígito, razón social, giro), dirección (principal, tributaria y de despacho) y los medios de
// contacto, más la lista de precios (sin ella no se le puede vender: LISTA_NO_DEFINIDA) y, si se
// quiere, el vendedor. Como en Random, la lista parte en la general (01P).
//
// Pedido de Rodrigo (2026-10-24): rápido para la venta en el local, pero completo y ordenado. Todo el
// texto se guarda en MAYÚSCULAS (los correos, en minúscula); el teléfono es obligatorio (hay que poder
// contactar al cliente) y el correo, si se digita, tiene que ser válido (2026-10-30: con solo el
// teléfono se crea, sin decirlo en pantalla). Tiene la forma de la ficha y nace en EFECTIVO /
// TRANSFERENCIA; la lista la elige quien lo crea y el resto de lo comercial, quien tenga el permiso.
//
// Si el RUT ya existe en el grupo (otra empresa, o como proveedor), no se duplica: se hace cliente de
// esta empresa CONSERVANDO lo que su relación ya tenga (proveedor, vendedor, lista, condición).
import { useQueryClient } from '@tanstack/react-query';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { ErrorApi, api, nuevaClaveIdempotencia } from '../api/cliente';
import {
  useFichaCliente,
  useListasPrecio,
  usePoliticaFicha,
  useVendedores,
} from '../api/consultas';
import type { CampoFicha, ClienteEncontrado, FichaEntidad } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import {
  asignacionesDe,
  CamposAsignaciones,
  CamposCredito,
  cambiosCredito,
  CONDICIONES_CLIENTE,
  creditoDe,
  pagoInmediato,
  problemasCredito,
  usePermisosAsignaciones,
  type Aplicacion,
  type Cambio,
  type ValoresAsignaciones,
  type ValoresCredito,
} from './FichaComercial';
import { FichaMaestro, type Seccion } from './FichaMaestro';
import { InformacionGeneral, type ValoresGenerales } from './InformacionGeneral';
import { Modal } from '../comun/Modal';
import { pesos, rut as formatoRut } from '../comun/formato';

const correoValido = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

/** Dígito verificador (módulo 11), el mismo cálculo del backend. */
export function dvDeRut(rut: string): string {
  let suma = 0;
  let mult = 2;
  for (let i = rut.length - 1; i >= 0; i--) {
    suma += Number(rut[i]) * mult;
    mult = mult === 7 ? 2 : mult + 1;
  }
  const resto = 11 - (suma % 11);
  return resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
}

/** La lista de venta por defecto de un cliente nuevo (la general, como TABPP01P en Random). */
export function useListaPorDefecto() {
  const listas = useListasPrecio('');
  const venta = (listas.data ?? []).filter((l) => l.uso === 'venta');
  return {
    venta,
    porDefecto: venta.find((l) => l.codigo === '01P')?.id ?? venta[0]?.id ?? '',
    cargando: listas.isLoading,
  };
}

/** La relación de la entidad con la empresa tal como la guarda el backend (con sus marcas). */
type Relacion = FichaEntidad['empresas'][number] & {
  es_proveedor?: boolean;
  iva_recuperable?: boolean;
  precios_con_iva?: boolean;
};

/** Una entidad del grupo que ya tiene ese RUT pero no es cliente de esta empresa. */
export interface EntidadExistente {
  id: string;
  razonSocial: string;
}

/**
 * Deja a la entidad como cliente de esta empresa. El vínculo se REEMPLAZA entero en el backend, así
 * que se parte de lo que ya tiene (si era proveedor lo sigue siendo; su vendedor, lista y condición
 * se conservan) y solo se completa lo que falta con `asignar`. Sin relación previa nace al contado.
 */
export async function vincularComoCliente(
  empresaId: string,
  entidadId: string,
  rut: string,
  asignar: { listaPrecioId?: string; vendedorId?: string; esProveedor?: boolean } = {},
): Promise<ClienteEncontrado> {
  const ficha = await api.get<FichaEntidad>(`/entidades/${entidadId}`);
  const r = ficha.empresas.find((x) => x.empresa_id === empresaId) as Relacion | undefined;
  const lista = r?.lista_precio_id ?? asignar.listaPrecioId;
  const vendedor = r?.vendedor_id ?? asignar.vendedorId;
  await api.put(`/entidades/${entidadId}/empresa`, {
    empresaId,
    esCliente: true,
    esProveedor: r?.es_proveedor ?? asignar.esProveedor ?? false,
    condicionPago: r?.condicion_pago ?? 'contado',
    plazoDias: r?.plazo_dias ?? 0,
    ...(r?.segmento ? { segmento: r.segmento } : {}),
    ...(r?.clasificacion ? { clasificacion: r.clasificacion } : {}),
    ...(lista ? { listaPrecioId: lista } : {}),
    ...(vendedor ? { vendedorId: vendedor } : {}),
    ivaRecuperable: r?.iva_recuperable ?? true,
    preciosConIva: r?.precios_con_iva ?? false,
  });
  const filas = await api.get<ClienteEncontrado[]>('/entidades', {
    query: { empresaId, rol: 'cliente', texto: rut, limite: '10' },
  });
  const c = filas.find((x) => x.id === entidadId);
  if (!c) throw new Error('Quedó vinculado, pero no aparece en la búsqueda: búscalo con F2');
  return c;
}

/** «¿Desea crearlo?» de Random: el código digitado no es de ningún cliente. */
export function ClienteNoExiste({
  empresaId,
  rut,
  dv,
  existente,
  puedeCrear,
  alCrear,
  alVinculado,
  alBuscar,
  alCerrar,
}: {
  empresaId: string;
  rut: string;
  dv: string | null;
  /** Ya está en el grupo con ese RUT: se ofrece hacerlo cliente, no crearlo de nuevo. */
  existente: EntidadExistente | null;
  puedeCrear: boolean;
  alCrear: () => void;
  alVinculado: (c: ClienteEncontrado) => void;
  alBuscar: () => void;
  alCerrar: () => void;
}) {
  const valido = !dv || dvDeRut(rut) === dv;
  const [error, setError] = useState<unknown>(null);
  const [vinculando, setVinculando] = useState(false);
  const listas = useListaPorDefecto();
  const vincular = async () => {
    if (!existente || vinculando) return;
    setVinculando(true);
    setError(null);
    try {
      alVinculado(
        await vincularComoCliente(empresaId, existente.id, rut, {
          ...(listas.porDefecto ? { listaPrecioId: listas.porDefecto } : {}),
        }),
      );
    } catch (e) {
      setError(e);
    } finally {
      setVinculando(false);
    }
  };
  return (
    <Modal titulo="Cliente no existe" ancho="angosto" ubicacion="media" alCerrar={alCerrar}>
      <p>
        No hay un cliente con el código{' '}
        <b className="mono">{formatoRut(rut, dv ?? dvDeRut(rut))}</b>.
      </p>
      {existente && (
        <p className="aviso-linea tono-info" data-existente={existente.id}>
          Ese RUT ya está en el grupo como <b>{existente.razonSocial}</b>, pero no es cliente de
          esta empresa. Se puede hacer cliente con los datos de su ficha (no se crea otro).
        </p>
      )}
      {!valido && (
        <p className="aviso-linea tono-malo">
          Ojo: el dígito verificador {dv} no corresponde a ese RUT (sería {dvDeRut(rut)}).
        </p>
      )}
      {!puedeCrear && (
        <p className="aviso-linea tono-aviso">
          No tienes permiso para crear clientes: pídeselo a tu supervisor, o búscalo por nombre.
        </p>
      )}
      <div className="acciones">
        <button type="button" onClick={alCerrar}>
          No (Esc)
        </button>
        <button
          type="button"
          onClick={alBuscar}
          {...(!puedeCrear ? { 'data-autofoco': true } : {})}
        >
          Buscar por nombre
        </button>
        {puedeCrear && existente && (
          <button
            type="button"
            className="primario"
            data-autofoco
            disabled={vinculando}
            onClick={() => void vincular()}
          >
            {vinculando ? 'Vinculando…' : 'Sí, hacerlo cliente (Enter)'}
          </button>
        )}
        {puedeCrear && !existente && (
          <button type="button" className="primario" data-autofoco onClick={alCrear}>
            Sí, crear cliente (Enter)
          </button>
        )}
      </div>
      {error !== null && <PanelError error={error} />}
    </Modal>
  );
}

/**
 * Alta de un cliente CON LA FORMA DE SU FICHA (Rodrigo, 2026-10-24): nace en efectivo /
 * transferencia con la lista general, y al crearlo se llena lo que el usuario puede asignar
 * (Rodrigo, 2026-10-30: «a mí no debería tenerme bloqueado»):
 *  - la lista de precios, quien crea el cliente (también sin permiso de crédito);
 *  - Asignaciones y Crédito, lo que el usuario cambia DIRECTO según la política de la ficha; lo que
 *    necesita firma se pide después en su ficha;
 *  - direcciones, contactos, cuentas y observaciones con «Crear y seguir»: se crea el cliente y la
 *    ventana pasa a su ficha.
 * Desde la venta se abre como ventana; desde Clientes, en pantalla.
 */
export function ClienteNuevo({
  empresaId,
  rutInicial,
  dvInicial,
  alCreado,
  alCerrar,
  enPantalla = false,
}: {
  empresaId: string;
  rutInicial: string;
  dvInicial: string | null;
  alCreado: (c: ClienteEncontrado) => void;
  alCerrar: () => void;
  /** En la pantalla Clientes (no como ventana sobre la venta). */
  enPantalla?: boolean;
}) {
  const qc = useQueryClient();
  const listas = useListaPorDefecto();
  const vendedores = useVendedores(empresaId);
  const politica = usePoliticaFicha(empresaId);
  const permisos = usePermisosAsignaciones();
  const [f, setF] = useState<ValoresGenerales>({
    rut: rutInicial,
    dv: dvInicial ?? (rutInicial ? dvDeRut(rutInicial) : ''),
    tipoEntidad: 'cliente',
    razonSocial: '',
    nombreFantasia: '',
    giro: '',
    calle: '',
    numero: '',
    comuna: '',
    referencia: '',
    telefono: '',
    correo: '',
    correoDte: '',
  });
  const [asig, setAsig] = useState(() => asignacionesDe(null));
  const [cred, setCred] = useState(() => creditoDe(null));
  const lista = asig.lista || listas.porDefecto;
  const [error, setError] = useState<unknown>(null);
  const [avance, setAvance] = useState<string | null>(null);
  /** Creado con «Crear y seguir»: la ventana muestra su ficha en esa sección. */
  const [creado, setCreado] = useState<{ cliente: ClienteEncontrado; seccion: Seccion } | null>(
    null,
  );
  const poner = (c: Partial<ValoresGenerales>) => setF((x) => ({ ...x, ...c }));
  const ponerAsig = (c: Partial<ValoresAsignaciones>) => setAsig((x) => ({ ...x, ...c }));
  const ponerCred = (c: Partial<ValoresCredito>) => setCred((x) => ({ ...x, ...c }));
  // El tipo de persona no se pregunta (Rodrigo, 2026-10-24): un RUT de 50 millones o más es empresa.
  const tipoPersona = Number(f.rut.replace(/\D/g, '') || 0) >= 50_000_000 ? 'juridica' : 'natural';
  const esProveedor = f.tipoEntidad === 'ambos';

  // Al crear, lo que tiene política se asigna solo si el usuario lo cambia directo. La lista la
  // elige quien crea el cliente (Rodrigo, 2026-10-30).
  const directo = (c: CampoFicha) =>
    politica.data?.find((p) => p.campo === c)?.aplicacion === 'DIRECTA';
  const aplicacion = (c: CampoFicha): Aplicacion =>
    c === 'lista_precio' || directo(c) ? 'DIRECTA' : 'NO_DISPONIBLE';
  const conCredito = directo('condicion_pago');
  const cambiosAlta: Cambio[] = [
    ...(asig.vendedor && directo('vendedor')
      ? [{ campo: 'vendedor' as const, valor: { vendedorId: asig.vendedor } }]
      : []),
    ...(conCredito ? cambiosCredito(cred, creditoDe(null)).filter((c) => directo(c.campo)) : []),
  ];
  const vacias = asignacionesDe(null);
  const comercialDirecto = (
    ['documento', 'dias', 'cobrador', 'segmento', 'clasificacion'] as const
  ).some((k) => asig[k] !== vacias[k]);

  const rutLimpio = f.rut.replace(/\D/g, '');
  const correo = f.correo.trim();
  const faltan = [
    !/^\d{7,9}$/.test(rutLimpio) && 'el RUT (7 a 9 dígitos)',
    rutLimpio && f.dv.toUpperCase() !== dvDeRut(rutLimpio) && 'un dígito verificador correcto',
    f.razonSocial.trim().length < 2 && 'la razón social',
    !f.calle.trim() && 'la calle de la dirección',
    !f.comuna && 'la comuna',
    !lista && 'la lista de precios',
    f.telefono.replace(/\D/g, '').length < 8 && 'el teléfono',
    correo && !correoValido(correo) && 'un correo válido',
    f.correoDte && !correoValido(f.correoDte.trim()) && 'un correo DTE válido',
  ].filter(Boolean) as string[];
  const problemas = conCredito ? problemasCredito(cred) : [];
  const incompleto = faltan.length > 0 || problemas.length > 0;

  /**
   * Dirección tributaria, teléfono y correos del formulario. Con la ficha de un RUT que ya existía,
   * solo lo que le falta: el backend deja al call center completar, no reemplazar (completar.ts).
   */
  const completarFicha = async (entidadId: string, ya: FichaEntidad | null) => {
    setAvance('Guardando dirección y contacto…');
    if (!ya?.direcciones.some((d) => d.activa && d.es_tributaria))
      await api.post(`/entidades/${entidadId}/direcciones`, {
        empresaId,
        calle: f.calle.trim(),
        ...(f.numero.trim() ? { numero: f.numero.trim() } : {}),
        comunaCodigo: f.comuna,
        ...(f.referencia.trim() ? { referencia: f.referencia.trim() } : {}),
        esPrincipal: true,
        esTributaria: true,
        esDespacho: true,
      });
    // Sin correo DTE propio, el de intercambio es el mismo correo del cliente.
    const correoDte = (f.correoDte.trim() || correo).toLowerCase();
    const medios = [
      f.telefono.trim() && { tipo: 'telefono', proposito: 'comercial', valor: f.telefono.trim() },
      correo && { tipo: 'correo', proposito: 'comercial', valor: correo.toLowerCase() },
      correoDte && { tipo: 'correo', proposito: 'dte', valor: correoDte },
    ].filter(Boolean) as { tipo: string; proposito: string; valor: string }[];
    for (const m of medios)
      if (
        !ya?.medios.some(
          (x) => x.activo !== false && x.tipo === m.tipo && x.proposito === m.proposito,
        )
      )
        await api.post(`/entidades/${entidadId}/medios`, { empresaId, ...m, principal: true });
  };
  const buscarCreado = async (entidadId: string) => {
    const filas = await api.get<ClienteEncontrado[]>('/entidades', {
      query: { empresaId, rol: 'cliente', texto: rutLimpio, limite: '10' },
    });
    const nuevo = filas.find((x) => x.id === entidadId);
    if (!nuevo)
      throw new Error('El cliente se creó, pero no aparece en la búsqueda: búscalo con F2');
    return nuevo;
  };

  const crear = async (seguir?: Seccion) => {
    if (incompleto || avance) return;
    setError(null);
    const listo = (c: ClienteEncontrado) =>
      seguir ? setCreado({ cliente: c, seccion: seguir }) : alCreado(c);
    try {
      setAvance('Creando el cliente…');
      let entidadId: string;
      try {
        const r = await api.post<{ entidadId: string }>(
          '/entidades',
          {
            empresaId,
            razonSocial: f.razonSocial.trim(),
            ...(f.nombreFantasia.trim() ? { nombreFantasia: f.nombreFantasia.trim() } : {}),
            ...(f.giro.trim() ? { giro: f.giro.trim() } : {}),
            tipoPersona,
            esCliente: true,
            rut: rutLimpio,
            dv: f.dv.toUpperCase(),
          },
          { idempotencia: nuevaClaveIdempotencia('cliente-nuevo') },
        );
        entidadId = r.entidadId;
      } catch (e) {
        // El RUT ya está en el grupo (otra empresa o proveedor): no se duplica.
        const existente =
          e instanceof ErrorApi
            ? (e.cuerpo.detalle?.['entidadId'] as string | undefined)
            : undefined;
        if (!existente) throw e;
        // Se hace cliente con SU ficha: lo digitado aquí no reemplaza lo que ya tiene (no se duplican
        // direcciones ni contactos), pero sí completa lo que le FALTA. 31-10: un alta que quedó a
        // medias se reintentaba y el cliente seguía sin dirección, sin poder facturarle.
        setAvance('El RUT ya existe en el grupo: haciéndolo cliente…');
        const yaExiste = await vincularComoCliente(empresaId, existente, rutLimpio, {
          listaPrecioId: lista,
          esProveedor,
        });
        await completarFicha(existente, await api.get<FichaEntidad>(`/entidades/${existente}`));
        await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
        listo(yaExiste);
        return;
      }
      setAvance('Asignando la lista de precios…');
      await vincularComoCliente(empresaId, entidadId, rutLimpio, {
        listaPrecioId: lista,
        esProveedor,
      });
      await completarFicha(entidadId, null);
      if (comercialDirecto) {
        setAvance('Guardando asignaciones…');
        const ficha = await api.get<FichaEntidad>(`/entidades/${entidadId}`);
        const rel = ficha.empresas.find((x) => x.empresa_id === empresaId);
        await api.patch(`/entidades/${entidadId}/comercial`, {
          empresaId,
          version: rel?.version ?? 1,
          esCliente: true,
          esProveedor,
          documentoPorDefecto: asig.documento || null,
          costoLogistico: !!rel?.costo_logistico,
          diasVisita: asig.dias.includes('X') ? asig.dias : null,
          cobradorId: asig.cobrador || null,
          segmento: asig.segmento.trim() || null,
          clasificacion: asig.clasificacion.trim() || null,
        });
      }
      // Vendedor y crédito van por la política de la ficha: el backend vuelve a exigir el permiso.
      if (cambiosAlta.length) {
        setAvance('Asignando vendedor y crédito…');
        await api.post(
          `/entidades/${entidadId}/cambios`,
          { empresaId, motivo: 'ALTA DEL CLIENTE', campos: cambiosAlta },
          { idempotencia: nuevaClaveIdempotencia('cliente-nuevo-ficha') },
        );
      }
      const nuevo = await buscarCreado(entidadId);
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
      listo(nuevo);
    } catch (e) {
      setError(e);
    } finally {
      setAvance(null);
    }
  };

  const teclas = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      void crear();
    } else if (e.key === 'Escape' && enPantalla) {
      e.preventDefault();
      alCerrar();
    }
  };

  const acciones = creado ? (
    <div className="acciones">
      <button
        type="button"
        className="primario"
        data-autofoco
        onClick={() => alCreado(creado.cliente)}
      >
        Listo (Esc)
      </button>
    </div>
  ) : (
    <div className="acciones">
      {incompleto && (
        <span className="ayuda">
          {faltan.length > 0 && `Falta ${faltan.join(', ')}. `}
          {problemas.join(' · ')}
        </span>
      )}
      <button type="button" onClick={alCerrar}>
        Cancelar (Esc)
      </button>
      <button
        type="button"
        className="primario"
        disabled={incompleto || !!avance}
        onClick={() => void crear()}
      >
        {avance ?? 'Crear cliente (Ctrl+Enter)'}
      </button>
    </div>
  );

  const condicion = CONDICIONES_CLIENTE.find(([v]) => v === cred.condicion)?.[1] ?? cred.condicion;
  const vendedor = (vendedores.data ?? []).find((v) => v.id === asig.vendedor);
  const informacion = (
    <form
      className="cliente-nuevo"
      onSubmit={(e) => {
        e.preventDefault();
        void crear();
      }}
      onKeyDown={teclas}
    >
      <InformacionGeneral
        v={f}
        poner={poner}
        alta
        editable
        alRut={(r) => poner({ rut: r, dv: r.length >= 7 ? dvDeRut(r) : '' })}
      />
      <fieldset className="recuadro-ficha" data-alta-condiciones>
        <legend>Cómo nace</legend>
        <div className="ficha-grilla">
          <div className="campo">
            <span className="etiqueta">Lista de precios</span>
            <select
              aria-label="Lista de precios"
              value={lista}
              onChange={(e) => ponerAsig({ lista: e.target.value })}
            >
              {listas.venta.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.codigo} · {l.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <span className="etiqueta">Condición de pago</span>
            <span>
              {condicion}
              {!pagoInmediato(cred.condicion) && ` · ${cred.plazo || 0} días`}
              {cred.linea && ` · línea ${pesos(cred.linea)}`}
            </span>
          </div>
          <div className="campo">
            <span className="etiqueta">Vendedor</span>
            <span>{vendedor ? `${vendedor.codigo} · ${vendedor.nombre}` : 'Sin vendedor'}</span>
          </div>
        </div>
        <p className="ayuda">
          {conCredito
            ? 'Vendedor, documento, visitas y crédito se eligen en las pestañas Asignaciones y Crédito.'
            : 'Documento y visitas se eligen en Asignaciones. La condición de pago y el crédito se piden después en su ficha, con autorización en línea (Firmas).'}
        </p>
      </fieldset>
      {error !== null && <PanelError error={error} />}
    </form>
  );
  /** Lo que se agrega a un cliente ya existente: se crea y la ventana pasa a su ficha. */
  const crearYSeguir = (s: Seccion, que: string) => (
    <div className="ficha-seccion" onKeyDown={teclas}>
      <fieldset className="recuadro-ficha">
        <legend>Con el cliente ya creado</legend>
        <p>{que} se agregan una vez creado el cliente: se crea y sigues aquí, en su ficha.</p>
        {faltan.length > 0 && (
          <p className="texto-aviso">Antes falta, en Información general: {faltan.join(', ')}.</p>
        )}
        <div className="acciones-ficha">
          <button
            type="button"
            className="primario"
            disabled={incompleto || !!avance}
            onClick={() => void crear(s)}
          >
            {avance ?? 'Crear el cliente y seguir aquí'}
          </button>
        </div>
      </fieldset>
      {error !== null && <PanelError error={error} />}
    </div>
  );
  const alta: Partial<Record<Seccion, ReactNode>> = {
    identificacion: informacion,
    asignaciones: (
      <div className="ficha-seccion" onKeyDown={teclas}>
        <CamposAsignaciones
          f={{ ...asig, lista }}
          poner={ponerAsig}
          vendedores={vendedores.data ?? []}
          listas={listas.venta}
          aplicacion={aplicacion}
          {...permisos}
          conTipo={false}
        />
      </div>
    ),
    ...(conCredito
      ? {
          credito: (
            <div className="ficha-seccion" onKeyDown={teclas}>
              <CamposCredito
                f={cred}
                poner={ponerCred}
                aplicacion={aplicacion}
                conBloqueo={false}
              />
              {problemas.length > 0 && <p className="texto-malo">{problemas.join(' · ')}</p>}
            </div>
          ),
        }
      : {}),
    direcciones: crearYSeguir('direcciones', 'Las direcciones de despacho y sucursales'),
    contactos: crearYSeguir('contactos', 'Los contactos, sus correos y teléfonos'),
    cuentas: crearYSeguir('cuentas', 'Las cuentas bancarias'),
    observaciones: crearYSeguir('observaciones', 'Las observaciones'),
  };
  const ficha = creado ? (
    <FichaDelCreado empresaId={empresaId} entidadId={creado.cliente.id} seccion={creado.seccion} />
  ) : (
    <FichaMaestro empresaId={empresaId} ficha={null} vendedores={[]} listas={[]} alta={alta} />
  );
  // Ya creado, cerrar la ventana es volver con el cliente.
  const cerrar = creado ? () => alCreado(creado.cliente) : alCerrar;

  if (enPantalla)
    return (
      <div className="pantalla alta-cliente" data-pantalla="cliente-nuevo">
        <header className="alta-cliente-cabecera">
          <h2>Cliente nuevo</h2>
          {acciones}
        </header>
        {ficha}
      </div>
    );
  return (
    <Modal titulo="Cliente nuevo" ancho="ancho" alCerrar={cerrar} pie={acciones}>
      {ficha}
    </Modal>
  );
}

/** «Crear y seguir»: la ficha del cliente recién creado, abierta en la sección elegida. */
function FichaDelCreado({
  empresaId,
  entidadId,
  seccion,
}: {
  empresaId: string;
  entidadId: string;
  seccion: Seccion;
}) {
  const ficha = useFichaCliente(empresaId, entidadId);
  const vendedores = useVendedores(empresaId);
  const listas = useListasPrecio(empresaId);
  if (ficha.isError) return <PanelError error={ficha.error} />;
  if (!ficha.data) return <p className="apagado">Abriendo la ficha del cliente…</p>;
  return (
    <FichaMaestro
      empresaId={empresaId}
      ficha={ficha.data}
      vendedores={vendedores.data ?? []}
      listas={listas.data ?? []}
      seccionInicial={seccion}
    />
  );
}
